import { Box, Chip, Typography } from '@mui/material';
import { useTagLookup } from '@/hooks/useTags';
import type { NamedTag } from '@/types';

interface TagChipsProps {
  /**
   * Tags, as documents and requirements carry them: bare slugs (resolved through
   * the vocabulary) or `{ slug, displayName }` (the public share view, which
   * cannot fetch the vocabulary).
   */
  slugs: readonly (string | NamedTag)[];
  /** Shown when the set is empty — a document with no tags is "Okategoriserad". */
  emptyLabel?: string | undefined;
  size?: 'small' | 'medium' | undefined;
  /** Render at most this many, then "+N". Omit to show all. */
  max?: number | undefined;
}

/**
 * Read-only tag chips (AG-249).
 *
 * Documents and requirements carry slugs; the display name is resolved here so
 * every surface that shows tags renders them identically. A slug missing from
 * the vocabulary falls back to itself rather than disappearing — a deactivated
 * tag is still information about the document.
 */
export default function TagChips({
  slugs,
  emptyLabel = 'Okategoriserad',
  size = 'small',
  max,
}: TagChipsProps) {
  const { displayName } = useTagLookup();

  if (slugs.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  const shown = max ? slugs.slice(0, max) : slugs;
  const hidden = slugs.length - shown.length;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {shown.map((tag) => {
        const slug = typeof tag === 'string' ? tag : tag.slug;
        return (
          <Chip
            key={slug}
            size={size}
            label={typeof tag === 'string' ? displayName(tag) : tag.displayName}
            variant="outlined"
          />
        );
      })}
      {hidden > 0 && <Chip size={size} label={`+${hidden}`} variant="outlined" />}
    </Box>
  );
}
